import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { getMyItems, getRentals, updateRentalStatus, createCheckout, deleteItem, getWishlist, exportRentals, toggleFeatureItem } from '../services/api';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Package, Clock, DollarSign, Plus, Edit, Trash2, Check, X, CreditCard, Eye, MessageCircle, Heart, Download, StarIcon, RotateCcw } from 'lucide-react';
import { getCategoryPlaceholder } from '../utils/placeholders';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getImageUrl } from '../services/api';
import ItemCard from '../components/ItemCard';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  
  const [myItems, setMyItems] = useState([]);
  const [rentalsAsRenter, setRentalsAsRenter] = useState([]);
  const [rentalsAsOwner, setRentalsAsOwner] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const defaultTab = searchParams.get('tab') || 'items';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [items, renterRentals, ownerRentals, wishlist] = await Promise.all([
        getMyItems(),
        getRentals('renter'),
        getRentals('owner'),
        getWishlist().catch(() => [])
      ]);
      setMyItems(items);
      setRentalsAsRenter(renterRentals);
      setRentalsAsOwner(ownerRentals);
      setWishlistItems(wishlist);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (rentalId, status) => {
    try {
      await updateRentalStatus(rentalId, status);
      toast.success(`Rental ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update rental status');
    }
  };

  const handlePayment = async (rentalId) => {
    try {
      const { url } = await createCheckout(rentalId);
      window.location.href = url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handleDeleteItem = async () => {
    if (!deleteItemId) return;
    try {
      await deleteItem(deleteItemId);
      toast.success('Item deleted');
      setDeleteItemId(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await exportRentals('csv');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `rental-history-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Rental history exported');
    } catch {
      toast.error('Failed to export');
    }
  };

  const handleFeatureToggle = async (itemId) => {
    try {
      const res = await toggleFeatureItem(itemId);
      toast.success(res.message);
      fetchData();
    } catch {
      toast.error('Failed to update');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      active: 'bg-purple-100 text-purple-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-[#596652]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Helmet>
        <title>Dashboard | NeighborBorrow</title>
        <meta name="description" content="Manage your items, rentals, wishlist, and rental requests on NeighborBorrow." />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-[#2C3329]">Dashboard</h1>
            <p className="text-[#596652]">Welcome back, {user?.full_name?.split(' ')[0]}</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="border-[#EBE8DF] rounded-full text-[#596652]"
              data-testid="export-rentals-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Link to="/list-item" data-testid="new-item-button">
              <Button className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547]">
                <Plus className="w-4 h-4 mr-2" />
                List New Item
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#596652] text-sm">My Items</p>
                <p className="text-2xl font-heading font-semibold text-[#2C3329]">{myItems.length}</p>
              </div>
              <div className="w-12 h-12 bg-[#8C9A76]/15 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-[#8C9A76]" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#596652] text-sm">Active Rentals</p>
                <p className="text-2xl font-heading font-semibold text-[#2C3329]">
                  {rentalsAsRenter.filter(r => ['approved', 'paid', 'active'].includes(r.status)).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#D97757]/15 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#D97757]" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#596652] text-sm">Rental Requests</p>
                <p className="text-2xl font-heading font-semibold text-[#2C3329]">
                  {rentalsAsOwner.filter(r => r.status === 'pending').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-[#F59E0B]/15 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-[#F59E0B]" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="bg-white border border-[#EBE8DF] rounded-xl p-1 mb-6">
            <TabsTrigger 
              value="items" 
              className="rounded-lg data-[state=active]:bg-[#8C9A76]/15 data-[state=active]:text-[#2C3329]"
              data-testid="tab-items"
            >
              My Items ({myItems.length})
            </TabsTrigger>
            <TabsTrigger 
              value="borrowing" 
              className="rounded-lg data-[state=active]:bg-[#8C9A76]/15 data-[state=active]:text-[#2C3329]"
              data-testid="tab-borrowing"
            >
              My Rentals ({rentalsAsRenter.length})
            </TabsTrigger>
            <TabsTrigger 
              value="lending" 
              className="rounded-lg data-[state=active]:bg-[#8C9A76]/15 data-[state=active]:text-[#2C3329]"
              data-testid="tab-lending"
            >
              Rental Requests ({rentalsAsOwner.length})
            </TabsTrigger>
            <TabsTrigger 
              value="wishlist" 
              className="rounded-lg data-[state=active]:bg-[#D97757]/15 data-[state=active]:text-[#D97757]"
              data-testid="tab-wishlist"
            >
              <Heart className="w-3.5 h-3.5 mr-1" />
              Wishlist ({wishlistItems.length})
            </TabsTrigger>
          </TabsList>

          {/* My Items Tab */}
          <TabsContent value="items">
            {myItems.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {myItems.map((item) => (
                  <div key={item.id} className="bg-white rounded-2xl border border-[#EBE8DF] overflow-hidden" data-testid={`my-item-${item.id}`}>
                    <div className="aspect-video bg-[#FDFBF7] overflow-hidden">
                      <img 
                        src={item.images?.[0] ? getImageUrl(item.images[0]) : getCategoryPlaceholder(item.category)}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = getCategoryPlaceholder(item.category);
                        }}
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-medium text-[#2C3329] line-clamp-1">{item.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${item.available ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {item.available ? 'Available' : 'Rented'}
                        </span>
                      </div>
                      <p className="text-[#D97757] font-semibold mb-3">${item.price_daily}/day</p>
                      <div className="flex gap-2">
                        <Link to={`/items/${item.id}`} className="flex-1">
                          <Button variant="outline" size="sm" className="w-full border-[#EBE8DF] rounded-lg" data-testid={`view-item-${item.id}`}>
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleFeatureToggle(item.id)}
                          className={`border-[#EBE8DF] rounded-lg ${item.featured ? 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30' : 'text-[#596652]'}`}
                          data-testid={`feature-item-${item.id}`}
                          title={item.featured ? 'Remove from featured' : 'Feature this item'}
                        >
                          <StarIcon className={`w-3 h-3 ${item.featured ? 'fill-current' : ''}`} />
                        </Button>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" className="border-[#EBE8DF] rounded-lg text-[#E53935]" onClick={() => setDeleteItemId(item.id)} data-testid={`delete-item-${item.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Delete Item</DialogTitle>
                            </DialogHeader>
                            <p className="text-[#596652]">Are you sure you want to delete "{item.title}"? This action cannot be undone.</p>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setDeleteItemId(null)}>Cancel</Button>
                              <Button onClick={handleDeleteItem} className="bg-[#E53935] text-white hover:bg-[#C62828]" data-testid="confirm-delete-button">
                                Delete
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#EBE8DF]">
                <Package className="w-12 h-12 text-[#EBE8DF] mx-auto mb-4" />
                <p className="text-[#596652] mb-4">You haven't listed any items yet</p>
                <Link to="/list-item">
                  <Button className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547]" data-testid="list-first-item-button">
                    List Your First Item
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>

          {/* My Rentals Tab (As Renter) */}
          <TabsContent value="borrowing">
            {rentalsAsRenter.length > 0 ? (
              <div className="space-y-4">
                {rentalsAsRenter.map((rental) => (
                  <div key={rental.id} className="bg-white rounded-2xl border border-[#EBE8DF] p-4 sm:p-6" data-testid={`rental-${rental.id}`}>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="w-full sm:w-32 h-24 bg-[#FDFBF7] rounded-xl overflow-hidden flex-shrink-0">
                        <img 
                          src={rental.item_image ? getImageUrl(rental.item_image) : getCategoryPlaceholder('Other')}
                          alt={rental.item_title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = getCategoryPlaceholder('Other');
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <Link to={`/items/${rental.item_id}`} className="font-medium text-[#2C3329] hover:text-[#D97757]">
                            {rental.item_title}
                          </Link>
                          {getStatusBadge(rental.status)}
                        </div>
                        <p className="text-sm text-[#596652] mb-1">
                          Owner: {rental.owner_name}
                        </p>
                        <p className="text-sm text-[#596652] mb-2">
                          {format(new Date(rental.start_date), 'MMM d')} - {format(new Date(rental.end_date), 'MMM d, yyyy')}
                        </p>
                        <p className="text-lg font-semibold text-[#D97757]">${rental.total_price}</p>
                      </div>
                      <div className="flex sm:flex-col gap-2">
                        {rental.status === 'approved' && (
                          <Button 
                            onClick={() => handlePayment(rental.id)}
                            className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547]"
                            data-testid={`pay-rental-${rental.id}`}
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Pay Now
                          </Button>
                        )}
                        {rental.status === 'completed' && (
                          <Link to={`/items/${rental.item_id}?rebook=1`}>
                            <Button className="w-full bg-[#8C9A76] text-white rounded-full hover:bg-[#7A8766]" data-testid={`rebook-rental-${rental.id}`}>
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Rebook
                            </Button>
                          </Link>
                        )}
                        <Link to={`/messages?user=${rental.owner_id}`}>
                          <Button variant="outline" className="w-full border-[#EBE8DF] rounded-full" data-testid={`message-owner-${rental.id}`}>
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Message
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#EBE8DF]">
                <Clock className="w-12 h-12 text-[#EBE8DF] mx-auto mb-4" />
                <p className="text-[#596652] mb-4">You haven't rented anything yet</p>
                <Link to="/browse">
                  <Button className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547]" data-testid="browse-items-button">
                    Browse Items
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>

          {/* Rental Requests Tab (As Owner) */}
          <TabsContent value="lending">
            {rentalsAsOwner.length > 0 ? (
              <div className="space-y-4">
                {rentalsAsOwner.map((rental) => (
                  <div key={rental.id} className="bg-white rounded-2xl border border-[#EBE8DF] p-4 sm:p-6" data-testid={`request-${rental.id}`}>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="w-full sm:w-32 h-24 bg-[#FDFBF7] rounded-xl overflow-hidden flex-shrink-0">
                        <img 
                          src={rental.item_image ? getImageUrl(rental.item_image) : getCategoryPlaceholder('Other')}
                          alt={rental.item_title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = getCategoryPlaceholder('Other');
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <span className="font-medium text-[#2C3329]">{rental.item_title}</span>
                          {getStatusBadge(rental.status)}
                        </div>
                        <p className="text-sm text-[#596652] mb-1">
                          Requested by: {rental.renter_name}
                        </p>
                        <p className="text-sm text-[#596652] mb-2">
                          {format(new Date(rental.start_date), 'MMM d')} - {format(new Date(rental.end_date), 'MMM d, yyyy')}
                        </p>
                        <p className="text-lg font-semibold text-[#D97757]">${rental.total_price}</p>
                      </div>
                      <div className="flex sm:flex-col gap-2">
                        {rental.status === 'pending' && (
                          <>
                            <Button 
                              onClick={() => handleStatusUpdate(rental.id, 'approved')}
                              className="bg-[#4CAF50] text-white rounded-full hover:bg-[#388E3C]"
                              data-testid={`approve-rental-${rental.id}`}
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Approve
                            </Button>
                            <Button 
                              onClick={() => handleStatusUpdate(rental.id, 'rejected')}
                              variant="outline"
                              className="border-[#E53935] text-[#E53935] rounded-full hover:bg-[#E53935]/10"
                              data-testid={`reject-rental-${rental.id}`}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Reject
                            </Button>
                          </>
                        )}
                        {rental.status === 'paid' && (
                          <Button 
                            onClick={() => handleStatusUpdate(rental.id, 'completed')}
                            className="bg-[#8C9A76] text-white rounded-full hover:bg-[#7A8A64]"
                            data-testid={`complete-rental-${rental.id}`}
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Mark Complete
                          </Button>
                        )}
                        <Link to={`/messages?user=${rental.renter_id}`}>
                          <Button variant="outline" className="w-full border-[#EBE8DF] rounded-full" data-testid={`message-renter-${rental.id}`}>
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Message
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#EBE8DF]">
                <DollarSign className="w-12 h-12 text-[#EBE8DF] mx-auto mb-4" />
                <p className="text-[#596652]">No rental requests yet</p>
              </div>
            )}
          </TabsContent>

          {/* Wishlist Tab */}
          <TabsContent value="wishlist">
            {wishlistItems.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="wishlist-grid">
                {wishlistItems.map((item) => (
                  <ItemCard key={item.id} item={item} wishlisted={true} onWishlistChange={(id, action) => {
                    if (action === 'removed') setWishlistItems(prev => prev.filter(i => i.id !== id));
                  }} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#EBE8DF]">
                <Heart className="w-12 h-12 text-[#EBE8DF] mx-auto mb-4" />
                <p className="text-[#596652] mb-2">Your wishlist is empty</p>
                <p className="text-[#596652]/60 text-sm mb-4">Save items you're interested in by clicking the heart icon</p>
                <Link to="/browse">
                  <Button className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547]" data-testid="browse-from-wishlist">
                    Browse Items
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DashboardPage;
